import { IValidateSignedDocumentResponse } from "common/api/signedDocument.service";
import { RecipientRecord } from "common/records/Recipient";
import { ISignedDocumentData } from "common/records/SignedDocument";
import { createAction } from "common/reducers/Utils";
import { ActionsUnion } from "common/reducers/UtilsTypes";
import { Nullable } from "common/types";
import { Map } from "immutable";

/**
 * Fixed slot id for the singleton in-progress signed-document draft. There is one draft at a time,
 * held at `signedDocuments.byId[DRAFT_SIGNED_DOCUMENT_ID]` with a single `currentId` pointing at it.
 * Every signature flow starts by resetting that slot via `initDraft` — a wholesale `receive()`
 * replace, not a merge — before it validates/configures/submits, and the server assigns a real id on
 * create. Because the draft is a singleton reset per flow (not a growing collection), a fixed sentinel
 * is correct here rather than the decrementing `unsavedId` convention used for collections
 * (document/estimate/fencing reducers).
 */
export const DRAFT_SIGNED_DOCUMENT_ID = -1;

export const FETCH_SIGNED_DOCUMENT = "@signedDocuments/FETCH_SIGNED_DOCUMENT";
export const RECEIVE_SIGNED_DOCUMENT = "@signedDocuments/RECEIVE_SIGNED_DOCUMENT";
export const RECEIVE_SIGNED_DOCUMENTS = "@signedDocuments/RECEIVE_SIGNED_DOCUMENTS";
export const SET_SIGNED_DOCUMENT_LOADED = "@signedDocuments/SET_SIGNED_DOCUMENT_LOADED";
export const SET_CURRENT_SIGNED_DOCUMENT_ID = "@signedDocuments/SET_CURRENT_SIGNED_DOCUMENT_ID";
export const CREATE_SIGNED_DOCUMENT = "@signedDocuments/CREATE_SIGNED_DOCUMENT";
export const INIT_RECIPIENTS = "@signedDocuments/INIT_RECIPIENTS";
export const INIT_RECIPIENT_INFO = "@signedDocuments/INIT_RECIPIENT_INFO";

export const SignedDocumentActions = {
  fetchSignedDocument: (signedDocumentId: number) => createAction(FETCH_SIGNED_DOCUMENT, signedDocumentId),
  receiveSignedDocument: (signedDocument: Partial<ISignedDocumentData>) =>
    createAction(RECEIVE_SIGNED_DOCUMENT, { signedDocument }),
  receiveSignedDocuments: (signedDocuments: Partial<ISignedDocumentData>[]) =>
    createAction(RECEIVE_SIGNED_DOCUMENTS, { signedDocuments }),
  setSignedDocumentLoaded: (signedDocumentId: number) => createAction(SET_SIGNED_DOCUMENT_LOADED, signedDocumentId),
  setCurrentSignedDocumentId: (signedDocumentId: number) =>
    createAction(SET_CURRENT_SIGNED_DOCUMENT_ID, signedDocumentId),
  initRecipients: (id: number, validations: Map<string, any>) => createAction(INIT_RECIPIENTS, { id, validations }),
  createSignedDocument: (
    response: IValidateSignedDocumentResponse,
    estimateId: Nullable<number>,
    metadata?: Record<string, unknown>,
  ) => createAction(CREATE_SIGNED_DOCUMENT, { response, estimateId, metadata }),
  initRecipientInfo: (recipient: RecipientRecord, id: number, index: number) =>
    createAction(INIT_RECIPIENT_INFO, { recipient, id, index }),
};

export type SignedDocumentActions = ActionsUnion<typeof SignedDocumentActions>;
